/**
 * EditableTranscript
 *
 * Displays transcript segments with tappable speaker pills and
 * inline-editable words. Silence segments render as subtle dividers.
 *
 * Supports split-segment and move-word operations via long-press
 * (mobile) or right-click context menu (web).
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    View,
    Text,
    Pressable,
    TextInput,
    StyleSheet,
    Platform,
} from 'react-native';
import { useTranslation } from '../../hooks/useTranslation';
import { TranscriptSegment } from '../../types';
import { brand } from '../../design-system/theme/tokens';

// ─── Design tokens ───────────────────────────────────────────────
const COLORS = {
    coach: { bg: '#EFF6FF', text: brand.primary },
    client: { bg: '#FFFBEB', text: '#92400E' },
    silence: { bg: '#F3F4F6', text: '#9CA3AF' },
    word: { bg: '#F9FAFB', border: '#E5E7EB', text: '#111827' },
    wordEditing: { bg: '#DBEAFE', border: '#3B82F6' },
    wordPickedUp: { bg: '#FEF3C7', border: '#F59E0B' },
    timestamp: { bg: '#F3F4F6', text: '#6B7280' },
    actionBar: { bg: '#F3F4F6', border: '#D1D5DB' },
    dropTarget: { border: '#3B82F6', bg: '#EFF6FF' },
    banner: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
    disabled: '#9CA3AF',
};

// ─── Helpers ─────────────────────────────────────────────────────
function formatTimestamp(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function speakerColors(speaker: string, speakers: string[]) {
    const idx = speakers.indexOf(speaker);
    return idx <= 0 ? COLORS.coach : COLORS.client;
}

// ─── WordChip ────────────────────────────────────────────────────
function WordChip({
    word,
    onEdit,
    onLongPress,
    isPickedUp,
    onCancelPickUp,
    moveMode,
}: {
    word: string;
    onEdit: (newWord: string) => void;
    onLongPress?: () => void;
    isPickedUp?: boolean;
    onCancelPickUp?: () => void;
    moveMode?: boolean;
}) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(word);

    // Sync value when word prop changes externally (split/move)
    useEffect(() => {
        if (!editing) setValue(word);
    }, [word, editing]);

    const commit = useCallback(() => {
        setEditing(false);
        const trimmed = value.trim();
        if (trimmed && trimmed !== word) {
            onEdit(trimmed);
        } else {
            setValue(word);
        }
    }, [value, word, onEdit]);

    // In move mode: picked word is tappable (cancels), others are inert
    if (moveMode) {
        if (isPickedUp && onCancelPickUp) {
            return (
                <Pressable
                    style={[styles.wordChip, styles.wordPickedUp]}
                    onPress={onCancelPickUp}
                    accessibilityLabel={word}
                    accessibilityHint="Tap to cancel move"
                >
                    <Text style={styles.wordText}>{word}</Text>
                </Pressable>
            );
        }
        // Non-picked words: plain View so presses pass to drop-target row
        return (
            <View style={styles.wordChip}>
                <Text style={styles.wordText}>{word}</Text>
            </View>
        );
    }

    if (editing) {
        return (
            <TextInput
                style={styles.wordInput}
                value={value}
                onChangeText={setValue}
                onBlur={commit}
                onSubmitEditing={commit}
                autoFocus
                selectTextOnFocus
            />
        );
    }

    const webContextMenu =
        Platform.OS === 'web' && onLongPress
            ? { onContextMenu: (e: any) => { e.preventDefault(); onLongPress(); } }
            : {};

    return (
        <Pressable
            style={styles.wordChip}
            onPress={() => setEditing(true)}
            onLongPress={onLongPress}
            delayLongPress={400}
            accessibilityLabel={word}
            accessibilityHint={
                onLongPress
                    ? 'Long press for actions, double tap to edit'
                    : 'Double tap to edit'
            }
            {...webContextMenu}
        >
            <Text style={styles.wordText}>{word}</Text>
        </Pressable>
    );
}

// ─── ActionBar ───────────────────────────────────────────────────
function ActionBar({
    wordIndex,
    onSplit,
    onMove,
    onDismiss,
    t,
}: {
    wordIndex: number;
    onSplit: () => void;
    onMove: () => void;
    onDismiss: () => void;
    t: (key: string, fallback?: string) => string;
}) {
    const splitDisabled = wordIndex === 0;

    return (
        <View style={styles.actionBar}>
            <Pressable
                style={[
                    styles.actionButton,
                    splitDisabled && styles.actionButtonDisabled,
                ]}
                onPress={splitDisabled ? undefined : onSplit}
                disabled={splitDisabled}
                accessibilityRole="button"
                accessibilityLabel={t('transcription.edit.splitHere')}
                accessibilityState={{ disabled: splitDisabled }}
            >
                <Text style={styles.actionIcon}>{'✂'}</Text>
                <Text
                    style={[
                        styles.actionLabel,
                        splitDisabled && styles.actionLabelDisabled,
                    ]}
                >
                    {t('transcription.edit.splitHere')}
                </Text>
            </Pressable>

            <View style={styles.actionDivider} />

            <Pressable
                style={styles.actionButton}
                onPress={onMove}
                accessibilityRole="button"
                accessibilityLabel={t('transcription.edit.move')}
            >
                <Text style={styles.actionIcon}>{'↕'}</Text>
                <Text style={styles.actionLabel}>
                    {t('transcription.edit.move')}
                </Text>
            </Pressable>

            <View style={styles.actionDivider} />

            <Pressable
                style={styles.actionButton}
                onPress={onDismiss}
                accessibilityRole="button"
                accessibilityLabel={t('common.cancel')}
            >
                <Text style={[styles.actionLabel, { color: COLORS.disabled }]}>
                    {'✕'}
                </Text>
            </Pressable>
        </View>
    );
}

// ─── PickUpBanner ────────────────────────────────────────────────
function PickUpBanner({
    word,
    onCancel,
    t,
}: {
    word: string;
    onCancel: () => void;
    t: (key: string, fallback?: string) => string;
}) {
    const message = t(
        'transcription.edit.tapToPlace',
    ).replace('{word}', word);

    return (
        <View
            style={styles.pickUpBanner}
            accessibilityRole="alert"
            accessibilityLiveRegion="polite"
        >
            <Text style={styles.pickUpBannerText}>{message}</Text>
            <Pressable
                onPress={onCancel}
                style={styles.pickUpBannerCancel}
                accessibilityRole="button"
                accessibilityLabel={t('common.cancel')}
            >
                <Text style={styles.pickUpBannerCancelText}>{'✕'}</Text>
            </Pressable>
        </View>
    );
}

// ─── SpeakerPill ─────────────────────────────────────────────────
function SpeakerPill({
    speaker,
    speakers,
    onToggle,
}: {
    speaker: string;
    speakers: string[];
    onToggle: () => void;
}) {
    const colors = speakerColors(speaker, speakers);

    return (
        <Pressable
            style={[styles.speakerPill, { backgroundColor: colors.bg }]}
            onPress={onToggle}
        >
            <Text style={[styles.speakerText, { color: colors.text }]}>
                {speaker}
            </Text>
        </Pressable>
    );
}

// ─── SilenceDivider ──────────────────────────────────────────────
function SilenceDivider({ duration }: { duration?: number }) {
    return (
        <View style={styles.silenceDivider}>
            <View style={styles.silenceLine} />
            {duration != null && (
                <Text style={styles.silenceLabel}>
                    {duration.toFixed(1)}s
                </Text>
            )}
            <View style={styles.silenceLine} />
        </View>
    );
}

// ─── SegmentRow ──────────────────────────────────────────────────
function SegmentRow({
    segment,
    segmentIndex,
    speakers,
    onWordEdit,
    onSpeakerToggle,
    onWordLongPress,
    activeWord,
    pickedWord,
    onSplit,
    onMoveStart,
    onDismissAction,
    onPlaceWord,
    onCancelPickUp,
    t,
}: {
    segment: TranscriptSegment;
    segmentIndex: number;
    speakers: string[];
    onWordEdit: (segIndex: number, wordIndex: number, newWord: string) => void;
    onSpeakerToggle: (segIndex: number) => void;
    onWordLongPress: (segIndex: number, wordIndex: number) => void;
    activeWord: { seg: number; word: number } | null;
    pickedWord: { seg: number; word: number; text: string } | null;
    onSplit: () => void;
    onMoveStart: () => void;
    onDismissAction: () => void;
    onPlaceWord: (segIndex: number) => void;
    onCancelPickUp: () => void;
    t: (key: string, fallback?: string) => string;
}) {
    const words = segment.text.split(/\s+/).filter(Boolean);
    const isActiveSegment = activeWord?.seg === segmentIndex;
    const isSourceSegment = pickedWord?.seg === segmentIndex;
    const isDropTarget = pickedWord != null && !isSourceSegment;
    const inMoveMode = pickedWord != null;

    const content = (
        <>
            <View style={styles.segmentHeader}>
                {segment.speaker && (
                    <SpeakerPill
                        speaker={segment.speaker}
                        speakers={speakers}
                        onToggle={() => onSpeakerToggle(segmentIndex)}
                    />
                )}
                <View style={styles.timestampBadge}>
                    <Text style={styles.timestampText}>
                        {formatTimestamp(segment.start)}
                    </Text>
                </View>
            </View>

            <View style={styles.wordsRow}>
                {words.map((w, wi) => (
                    <WordChip
                        key={`${segmentIndex}-${wi}`}
                        word={w}
                        onEdit={(nw) => onWordEdit(segmentIndex, wi, nw)}
                        onLongPress={
                            inMoveMode
                                ? undefined
                                : () => onWordLongPress(segmentIndex, wi)
                        }
                        isPickedUp={
                            pickedWord?.seg === segmentIndex &&
                            pickedWord?.word === wi
                        }
                        onCancelPickUp={onCancelPickUp}
                        moveMode={inMoveMode}
                    />
                ))}
            </View>

            {isActiveSegment && activeWord && (
                <ActionBar
                    wordIndex={activeWord.word}
                    onSplit={onSplit}
                    onMove={onMoveStart}
                    onDismiss={onDismissAction}
                    t={t}
                />
            )}
        </>
    );

    // Drop target: Pressable row so tapping anywhere places the word.
    // Normal mode: plain View so children (WordChip, SpeakerPill) receive
    // their own press events without interference from a disabled Pressable
    // (which on web sets pointer-events:none, blocking child clicks).
    if (isDropTarget) {
        return (
            <Pressable
                style={[styles.segmentRow, styles.segmentDropTarget]}
                onPress={() => onPlaceWord(segmentIndex)}
            >
                {content}
            </Pressable>
        );
    }

    return <View style={styles.segmentRow}>{content}</View>;
}

// ─── EditableTranscript (main) ───────────────────────────────────
interface EditableTranscriptProps {
    segments: TranscriptSegment[];
    onSegmentChange: (segIndex: number, newText: string) => void;
    onSpeakerToggle: (segIndex: number) => void;
    onSplitSegment: (segIndex: number, wordIndex: number) => void;
    onMoveWord: (fromSegIndex: number, wordIndex: number, toSegIndex: number) => void;
    speakers: string[];
}

export function EditableTranscript({
    segments,
    onSegmentChange,
    onSpeakerToggle,
    onSplitSegment,
    onMoveWord,
    speakers,
}: EditableTranscriptProps) {
    const { t } = useTranslation();
    const [activeWord, setActiveWord] = useState<{
        seg: number;
        word: number;
    } | null>(null);
    const [pickedWord, setPickedWord] = useState<{
        seg: number;
        word: number;
        text: string;
    } | null>(null);

    // Clear interaction state whenever segments change (split, move, edit, etc.)
    const prevSegmentsRef = useRef(segments);
    useEffect(() => {
        if (prevSegmentsRef.current !== segments) {
            prevSegmentsRef.current = segments;
            setActiveWord(null);
            setPickedWord(null);
        }
    }, [segments]);

    const handleWordEdit = useCallback(
        (segIndex: number, wordIndex: number, newWord: string) => {
            const seg = segments[segIndex];
            const words = seg.text.split(/\s+/).filter(Boolean);
            words[wordIndex] = newWord;
            onSegmentChange(segIndex, words.join(' '));
            setActiveWord(null);
            setPickedWord(null);
        },
        [segments, onSegmentChange],
    );

    const handleWordLongPress = useCallback(
        (segIndex: number, wordIndex: number) => {
            setActiveWord({ seg: segIndex, word: wordIndex });
        },
        [],
    );

    const handleSplit = useCallback(() => {
        if (!activeWord || activeWord.word === 0) return;
        onSplitSegment(activeWord.seg, activeWord.word);
        setActiveWord(null);
    }, [activeWord, onSplitSegment]);

    const handleMoveStart = useCallback(() => {
        if (!activeWord) return;
        const seg = segments[activeWord.seg];
        const words = seg.text.split(/\s+/).filter(Boolean);
        const text = words[activeWord.word] || '';
        setPickedWord({ seg: activeWord.seg, word: activeWord.word, text });
        setActiveWord(null);
    }, [activeWord, segments]);

    const handlePlaceWord = useCallback(
        (toSegIndex: number) => {
            if (!pickedWord) return;
            onMoveWord(pickedWord.seg, pickedWord.word, toSegIndex);
            setPickedWord(null);
        },
        [pickedWord, onMoveWord],
    );

    const handleDismiss = useCallback(() => {
        setActiveWord(null);
        setPickedWord(null);
    }, []);

    return (
        <View style={styles.container}>
            {pickedWord && (
                <PickUpBanner
                    word={pickedWord.text}
                    onCancel={handleDismiss}
                    t={t}
                />
            )}
            {segments.map((seg, idx) => {
                if (seg.is_silence) {
                    return (
                        <SilenceDivider
                            key={`silence-${idx}`}
                            duration={seg.silence_duration}
                        />
                    );
                }
                return (
                    <SegmentRow
                        key={`seg-${idx}`}
                        segment={seg}
                        segmentIndex={idx}
                        speakers={speakers}
                        onWordEdit={handleWordEdit}
                        onSpeakerToggle={onSpeakerToggle}
                        onWordLongPress={handleWordLongPress}
                        activeWord={activeWord}
                        pickedWord={pickedWord}
                        onSplit={handleSplit}
                        onMoveStart={handleMoveStart}
                        onDismissAction={handleDismiss}
                        onPlaceWord={handlePlaceWord}
                        onCancelPickUp={handleDismiss}
                        t={t}
                    />
                );
            })}
        </View>
    );
}

// ─── Styles ──────────────────────────────────────────────────────
const styles = StyleSheet.create({
    container: {
        gap: 4,
    },
    segmentRow: {
        backgroundColor: 'white',
        borderRadius: 8,
        padding: 12,
        gap: 8,
        borderWidth: 1,
        borderColor: 'transparent',
    },
    segmentDropTarget: {
        borderColor: COLORS.dropTarget.border,
        borderStyle: 'dashed',
        backgroundColor: COLORS.dropTarget.bg,
    },
    segmentHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    speakerPill: {
        paddingVertical: 4,
        paddingHorizontal: 10,
        borderRadius: 12,
    },
    speakerText: {
        fontSize: 12,
        fontWeight: '700',
    },
    timestampBadge: {
        backgroundColor: COLORS.timestamp.bg,
        paddingVertical: 2,
        paddingHorizontal: 6,
        borderRadius: 4,
    },
    timestampText: {
        fontSize: 11,
        color: COLORS.timestamp.text,
        fontVariant: ['tabular-nums'],
    },
    wordsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 4,
    },
    wordChip: {
        backgroundColor: COLORS.word.bg,
        borderWidth: 1,
        borderColor: COLORS.word.border,
        borderRadius: 4,
        paddingVertical: 3,
        paddingHorizontal: 6,
    },
    wordPickedUp: {
        backgroundColor: COLORS.wordPickedUp.bg,
        borderColor: COLORS.wordPickedUp.border,
        borderStyle: 'dashed',
        borderWidth: 2,
    },
    wordText: {
        fontSize: 14,
        color: COLORS.word.text,
    },
    wordInput: {
        backgroundColor: COLORS.wordEditing.bg,
        borderWidth: 1,
        borderColor: COLORS.wordEditing.border,
        borderRadius: 4,
        paddingVertical: 3,
        paddingHorizontal: 6,
        fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
        color: COLORS.word.text,
        minWidth: 40,
    },
    silenceDivider: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 6,
        paddingHorizontal: 16,
        gap: 8,
    },
    silenceLine: {
        flex: 1,
        height: 1,
        backgroundColor: '#E5E7EB',
    },
    silenceLabel: {
        fontSize: 11,
        color: COLORS.silence.text,
    },
    // Action Bar
    actionBar: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.actionBar.bg,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: COLORS.actionBar.border,
        paddingVertical: 4,
        paddingHorizontal: 2,
        alignSelf: 'flex-start',
    },
    actionButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 6,
        paddingHorizontal: 10,
        gap: 4,
    },
    actionButtonDisabled: {
        opacity: 0.4,
    },
    actionIcon: {
        fontSize: 14,
    },
    actionLabel: {
        fontSize: 13,
        fontWeight: '600',
        color: COLORS.word.text,
    },
    actionLabelDisabled: {
        color: COLORS.disabled,
    },
    actionDivider: {
        width: 1,
        height: 20,
        backgroundColor: COLORS.actionBar.border,
    },
    // Pick-up Banner
    pickUpBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.banner.bg,
        borderWidth: 1,
        borderColor: COLORS.banner.border,
        borderRadius: 8,
        paddingVertical: 8,
        paddingHorizontal: 12,
        gap: 8,
    },
    pickUpBannerText: {
        flex: 1,
        fontSize: 13,
        fontWeight: '600',
        color: COLORS.banner.text,
    },
    pickUpBannerCancel: {
        padding: 4,
    },
    pickUpBannerCancelText: {
        fontSize: 16,
        fontWeight: '700',
        color: COLORS.banner.text,
    },
});

export default EditableTranscript;
